import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Shell from "../components/Shell";
import VertragDokumentVorschau from "../components/VertragDokumentVorschau";
import LastschriftKarte from "../components/LastschriftKarte";
import JetztDran from "../components/vertrag/JetztDran";
import { Sektion, Sprungleiste, zuSektion } from "../components/vertrag/Sektion";
import { TabLeistungen, TabJahresplan, TabWirtschaft } from "../components/vertrag/Leistungen";
import { TabEinsaetze } from "../components/vertrag/Einsaetze";
import { TabAbrechnung } from "../components/vertrag/Abrechnung";
import { Ausfertigungskarte, Fristenkarte, Nachtragskarte } from "../components/vertrag/Akte";
import Kundenlink from "../components/vertrag/Kundenlink";
import RatenListe from "../components/RatenListe";
import { useVertrag, useProbelauf, eur, MONAT_KURZ, VERTRAG_STATUS_LABEL } from "../hooks/useVertraege";
import type { VertragDetail } from "../hooks/useVertraege";
import { zahlweiseLabel } from "../lib/labels";
import { zahl } from "../lib/zahl";
import "@vimaana/ui/kopf";

/**
 * Vertragsseite — aufgebaut wie eine Projektseite in Kontor (seit 09.10.2026):
 * Kopfzeile mit „⋯", Karte „Jetzt dran" mit Phasenband, eine Zahlenzeile,
 * darunter aufklappbare Sektionen; am Telefon eine Sprungleiste.
 *
 * Bis dahin: vier Kopfknöpfe, fünf Kennzahlkacheln, bis zu sieben Karten
 * untereinander und erst darunter fünf Reiter. Die Teile sind unverändert
 * übernommen (components/vertrag/*) — verschoben, nicht umgeschrieben.
 */
export default function VertragDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: v, isLoading } = useVertrag(id);

  if (isLoading || !v) {
    return (
      <Shell title="Vertrag" action={<Link className="pf-btn is-ghost" to="/vertraege">‹ Verträge</Link>}>
        <div className="pf-empty">{isLoading ? "Lade…" : "Nicht gefunden."}</div>
      </Shell>
    );
  }
  return <Akte v={v} />;
}

function Akte({ v }: { v: VertragDetail }) {
  const entwurf = v.status === "entwurf";
  const offenesAngebot = entwurf || v.status === "angeboten";
  const plan = monateImPlan(v);
  const kunde = v.erstes_jahr && !v.erstes_jahr.volljahr ? v.erstes_jahr : null;

  const ziele = [
    { anker: "leistungen", label: "Leistungen" },
    ...(!entwurf ? [{ anker: "einsaetze", label: "Einsätze" }, { anker: "zahlungen", label: "Zahlungen" }] : []),
    ...(!entwurf ? [{ anker: "abrechnung", label: "Abrechnung" }, { anker: "fristen", label: "Fristen" }] : []),
    ...(v.status === "aktiv" ? [{ anker: "nachtrag", label: "Nachtrag" }] : []),
    ...(!entwurf ? [{ anker: "dokumente", label: "Dokumente" }] : []),
    { anker: "wirtschaft", label: "Wirtschaftlichkeit" },
    { anker: "vorschau", label: "Kundenansicht" },
  ];

  return (
    <Shell title={v.kunde_name || v.nummer} ohneKopf>
      <div className="pk">
        <Kopfzeile v={v} />
        <JetztDran v={v} />
        <div className="pk-zahlen-block">
          <dl className="pk-zahlen">
            {kunde ? (
              <div className="pk-zahl is-akzent"><dt>Kunde zahlt {kunde.jahr}</dt><dd>{eur(kunde.summe_brutto)} brutto</dd></div>
            ) : null}
            <div className="pk-zahl"><dt>Monatsrate</dt><dd>{v.monats_netto ? `${eur(v.monats_netto)} netto` : "—"}</dd></div>
            <div className="pk-zahl"><dt>Jahreswert</dt><dd>{eur(v.jahres_netto)} netto</dd></div>
            <div className="pk-zahl"><dt>DB je Stunde</dt><dd>{v.kalkulation?.db_pro_stunde ? eur(v.kalkulation.db_pro_stunde) : "—"}</dd></div>
            <div className="pk-zahl"><dt>Zahlweise</dt><dd>{zahlweiseLabel(v.zahlweise)}</dd></div>
          </dl>
        </div>
      </div>

      <Sprungleiste ziele={ziele} />

      <Sektion titel="Leistungen und Jahresplan" anker="leistungen" standardOffen={offenesAngebot}
        kopfRechts={`${v.leistungen.length} ${v.leistungen.length === 1 ? "Leistung" : "Leistungen"}${plan ? ` · ${plan}` : ""} · Faktor ×${zahl(v.faktor, 2)}`}>
        <TabLeistungen v={v} />
        <div className="pf-seclabel">Jahresplan</div>
        <TabJahresplan v={v} />
      </Sektion>

      {!entwurf && (
        <Sektion titel="Einsätze" anker="einsaetze"
          kopfRechts={v.ist ? `${v.ist.einsaetze_erledigt} von ${v.ist.einsaetze_gesamt} erledigt` : undefined}>
          <TabEinsaetze v={v} />
        </Sektion>
      )}

      {!entwurf && (
        <Sektion titel="Raten und Zahlungen" anker="zahlungen"
          kopfRechts={v.ist ? `${v.ist.raten_bezahlt} ${v.ist.raten_bezahlt === 1 ? "Rate" : "Raten"} bezahlt · ${eur(v.ist.umsatz_bezahlt)}` : zahlweiseLabel(v.zahlweise)}>
          {kunde && <p className="pk-hinweis" style={{ marginBottom: 10 }}>{kunde.zeile}{kunde.folgejahr_zeile ? ` · ${kunde.folgejahr_zeile}` : ""}</p>}
          <RatenListe vertragId={v.id} />
          <div className="pf-seclabel">Lastschrift-Mandat</div>
          <LastschriftKarte vertragId={v.id} status={v.status} />
          <Link to="/zahlungen" className="pf-linkbtn" style={{ fontSize: 13 }}>Alle fälligen Raten und Einzüge →</Link>
        </Sektion>
      )}

      {!entwurf && (
        <Sektion titel="Mehraufwand und Abrechnung" anker="abrechnung">
          <TabAbrechnung v={v} />
        </Sektion>
      )}

      {!entwurf && (
        <Sektion titel="Laufzeit und Fristen" anker="fristen"
          kopfRechts={v.gekuendigt_zum ? `gekündigt zum ${new Date(v.gekuendigt_zum).toLocaleDateString("de-DE")}` : `${v.kuendigungsfrist_monate} Mon. Kündigungsfrist`}>
          <Fristenkarte v={v} />
        </Sektion>
      )}

      {v.status === "aktiv" && (
        <Sektion titel="Nachtrag und Preisanpassung" anker="nachtrag"
          kopfRechts={v.preisanpassung_zum ? `angekündigt zum ${new Date(v.preisanpassung_zum).toLocaleDateString("de-DE")}` : undefined}>
          <Nachtragskarte v={v} />
        </Sektion>
      )}

      {!entwurf && (
        <Sektion titel="Dokumente und Kundenlink" anker="dokumente"
          kopfRechts={v.ausfertigung ? `festgeschrieben · Vorlage V${v.ausfertigung.vorlage_version}` : "noch nicht festgeschrieben"}>
          <Ausfertigungskarte v={v} />
          <Kundenlink kundeId={v.kunde_id} />
        </Sektion>
      )}

      <Sektion titel="Wirtschaftlichkeit" anker="wirtschaft"
        kopfRechts={v.kalkulation ? `DB ${eur(v.kalkulation.deckungsbeitrag)} im Jahr` : undefined}>
        <TabWirtschaft v={v} />
      </Sektion>

      {/* Zugeklappt, weil die Vorschau beim Öffnen ein PDF misst. */}
      <Sektion titel="So sieht es der Kunde" anker="vorschau">
        <VertragDokumentVorschau vertragId={v.id} />
      </Sektion>
    </Shell>
  );
}

/** „Apr – Nov" aus den beauftragten Leistungen. */
function monateImPlan(v: VertragDetail): string {
  const m = new Set<number>();
  for (const l of v.leistungen) if (!l.optional || l.beauftragt) for (const x of l.monate) m.add(x);
  const s = [...m].sort((a, b) => a - b);
  if (!s.length) return "";
  return s.length === 1 ? MONAT_KURZ[s[0] - 1] : `${MONAT_KURZ[s[0] - 1]} – ${MONAT_KURZ[s[s.length - 1] - 1]}`;
}

/** Kopfzeile wie Kontors Projektkopf: zurück, Name, Unterzeile, „⋯". */
function Kopfzeile({ v }: { v: VertragDetail }) {
  const navigate = useNavigate();
  const probelauf = useProbelauf(v.id);
  const [menue, setMenue] = useState(false);
  const knopf = useRef<HTMLButtonElement>(null);
  const kennung = v.nummer?.startsWith("ENTWURF-") ? "Entwurf" : v.nummer;
  const status = VERTRAG_STATUS_LABEL[v.status] ?? v.status;
  const offenesAngebot = v.status === "entwurf" || v.status === "angeboten";

  useEffect(() => {
    if (!menue) return;
    const zu = (e: KeyboardEvent) => { if (e.key === "Escape") { setMenue(false); knopf.current?.focus(); } };
    document.addEventListener("keydown", zu);
    return () => document.removeEventListener("keydown", zu);
  }, [menue]);

  function punkt(text: string, tun: () => void) {
    return (
      <button type="button" className="pk-menue-punkt" role="menuitem"
        onClick={() => { setMenue(false); tun(); }}>{text}</button>
    );
  }

  return (
    <div className="pk-zeile">
      <Link to="/vertraege" className="pk-zurueck" aria-label="Zurück zu den Verträgen" style={{ textDecoration: "none" }}>
        <span aria-hidden="true">‹</span><span className="pk-zurueck-text">Verträge</span>
      </Link>
      <div className="pk-titelblock">
        <h1 className="pk-titel">
          <Link to={`/kunden?kunde=${encodeURIComponent(v.kunde_id)}`} style={{ color: "inherit", textDecoration: "none" }}>
            {v.kunde_name || kennung}
          </Link>
        </h1>
        <p className="pk-unter">
          {kennung}{status === kennung ? "" : ` · ${status}`}{v.version > 1 ? ` · Fassung ${v.version}` : ""}
          {v.vertragsart === "unternehmer" ? " · Firmenkunde" : " · Privatkunde"}
        </p>
      </div>
      <div className="pk-menue">
        <button ref={knopf} type="button" className="pk-rund" aria-haspopup="menu" aria-expanded={menue}
          aria-label="Weitere Aktionen" onClick={() => setMenue(m => !m)}>⋯</button>
        {menue && (
          <>
            <div className="pf-menue-hinterhang" onClick={() => setMenue(false)} />
            <div className="pk-menue-liste" role="menu">
              {punkt("So sieht es der Kunde", () => zuSektion("vorschau"))}
              {offenesAngebot && punkt(v.status === "entwurf" ? "Weiterbearbeiten" : "Angebot ändern",
                () => navigate(`/vertraege/neu?entwurf=${v.id}`))}
              {/* Den Funnel durchklicken, ohne den Vertrag anzufassen. */}
              {offenesAngebot && punkt(probelauf.isPending ? "Öffne…" : "Probelauf als Kunde", () => {
                probelauf.mutateAsync().then(r => { window.open(r.url, "_blank", "noreferrer"); }).catch(() => undefined);
              })}
              {v.status === "aktiv" && punkt("Nachtrag anlegen", () => zuSektion("nachtrag"))}
              {v.status !== "entwurf" && punkt("Dokumente und Kundenlink", () => zuSektion("dokumente"))}
              {v.status !== "entwurf" && punkt("Laufzeit und Fristen", () => zuSektion("fristen"))}
              <div className="pk-menue-trenner" />
              {punkt("Zum Kunden", () => navigate(`/kunden?kunde=${encodeURIComponent(v.kunde_id)}`))}
              {punkt("Kunde in Kontor öffnen", () => {
                window.open(`https://kontor.vimaanagarten.de/kunden?kunde=${encodeURIComponent(v.kunde_id)}`, "_blank", "noreferrer");
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
